import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowRight, Film, Images, RefreshCw, Upload, X } from 'lucide-react'
import {
  slideshowApi,
  type MusicTrack,
  type SlideshowPreview,
  type SlideshowSettings as SlideshowSettingsDto,
  type SlideshowTransition,
  type SlideshowVideoSlot,
} from '../../lib/infoserviceSlideshowApi.js'
import { infoservicesApi, type InfoserviceResolution } from '../../lib/adsApi.js'
import { infoserviceCgApi } from '../../lib/settingsApi.js'
import { useHasPermission } from '../../store/index.js'
import { cgTemplatesApi } from '../../lib/api.js'
import type { CgTemplate } from '../../types/domain.js'
import type { ProbeResult } from '../../lib/mediaBrowserApi.js'
import {
  editableOf,
  formatSeconds,
  formatSpan,
  sameEditable,
  type EditableConfig,
} from '../../lib/slideshowSettingsForm.js'
import { MediaBrowserModal } from '../programs/MediaBrowserModal.js'
import { Spinner } from '../common/Spinner.js'
import { SlideshowTimeline } from './SlideshowTimeline.js'
import { TransitionEditor } from './TransitionEditor.js'
import { MusicPlaylist } from './MusicPlaylist.js'

interface SlideshowSettingsProps {
  channelId: string | null
  canManage: boolean
  onShowAnnouncements: () => void
}

interface CgForm { templateId: string; offsetSec: number; hideBeforeEndSec: number }

type Picker = SlideshowVideoSlot | 'mp4' | null

const PICKER_LABELS: Record<Exclude<Picker, null>, string> = {
  intro: 'Choisir l’intro',
  outro: 'Choisir l’outro',
  introStinger: 'Choisir le stinger',
  announcementStinger: 'Choisir le stinger',
  mp4: 'Choisir le MP4 du jour',
}

/** Dossier des copies normalisées, relatif à la racine média (`MUSIC_DIR` côté serveur). */
const MUSIC_FOLDER = 'INFOSERVICES/MUSIQUE'

const errorText = (cause: unknown, fallback: string) => (cause instanceof Error && cause.message ? cause.message : fallback)

const sameCg = (a: CgForm, b: CgForm) => a.templateId === b.templateId && a.offsetSec === b.offsetSec && a.hideBeforeEndSec === b.hideBeforeEndSec

/**
 * Infoservice › Réglages (maquette `reglages-v2`). Les fichiers (intro, outro,
 * stinger, morceaux, MP4 du jour) s'enregistrent dès qu'on les choisit ; le
 * reste suit « Enregistrer ». L'aperçu montre les réglages ENREGISTRÉS.
 */
export const SlideshowSettings = memo(function SlideshowSettings({ channelId, canManage, onShowAnnouncements }: SlideshowSettingsProps) {
  const canEditGrid = useHasPermission('GRID_ITEM_EDIT')
  const [settings, setSettings] = useState<SlideshowSettingsDto | null>(null)
  const [form, setForm] = useState<EditableConfig | null>(null)
  const [preview, setPreview] = useState<SlideshowPreview | null>(null)
  const [seed, setSeed] = useState<number | undefined>(undefined)
  const [mp4, setMp4] = useState<InfoserviceResolution | null>(null)
  const [templates, setTemplates] = useState<CgTemplate[]>([])
  const [cg, setCg] = useState<CgForm>({ templateId: '', offsetSec: 0, hideBeforeEndSec: 0 })
  const [cgSaved, setCgSaved] = useState<CgForm>({ templateId: '', offsetSec: 0, hideBeforeEndSec: 0 })
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [uploading, setUploading] = useState<string | null>(null)
  const [musicPicker, setMusicPicker] = useState(false)
  const [restoring, setRestoring] = useState<string | null>(null)
  const [picker, setPicker] = useState<Picker>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'warning' | 'error'; text: string } | null>(null)

  const refreshPreview = useCallback(async (nextSeed?: number) => {
    if (!channelId) return
    try {
      setPreview(await slideshowApi.preview(channelId, { seed: nextSeed }))
    } catch {
      setPreview(null)
    }
  }, [channelId])

  /** Après un enregistrement de fichier, on garde les saisies en cours. */
  const adoptSettings = useCallback((next: SlideshowSettingsDto, keepEdits: boolean) => {
    setSettings(next)
    setForm(current => (keepEdits && current ? { ...current } : editableOf(next.config)))
  }, [])

  useEffect(() => {
    if (!channelId) return
    let cancelled = false
    setLoading(true)
    setMessage(null)
    Promise.all([
      slideshowApi.get(channelId),
      infoserviceCgApi.get(channelId),
      cgTemplatesApi.list(channelId),
      infoservicesApi.resolve({ channelId }).catch(() => null),
    ])
      .then(([loaded, cgConfig, list, resolution]) => {
        if (cancelled) return
        adoptSettings(loaded, false)
        const cgForm = { templateId: cgConfig.templateId ?? '', offsetSec: cgConfig.offsetSec, hideBeforeEndSec: cgConfig.hideBeforeEndSec }
        setCg(cgForm)
        setCgSaved(cgForm)
        setTemplates(list.filter(template => template.active))
        setMp4(resolution)
      })
      .catch(cause => { if (!cancelled) setMessage({ tone: 'error', text: errorText(cause, 'Chargement impossible') }) })
      .finally(() => { if (!cancelled) setLoading(false) })
    void refreshPreview()
    return () => { cancelled = true }
  }, [adoptSettings, channelId, refreshPreview])

  const saved = useMemo(() => (settings ? editableOf(settings.config) : null), [settings])
  const dirty = Boolean(form && saved && !sameEditable(form, saved)) || !sameCg(cg, cgSaved)
  const set = useCallback(<K extends keyof EditableConfig>(key: K, value: EditableConfig[K]) => {
    setForm(current => (current ? { ...current, [key]: value } : current))
    setMessage(null)
  }, [])

  const save = useCallback(async () => {
    if (!channelId || !form || !saved) return
    setBusy('save')
    setMessage(null)
    try {
      if (!sameCg(cg, cgSaved)) {
        const next = await infoserviceCgApi.update(channelId, { templateId: cg.templateId || null, offsetSec: cg.offsetSec, hideBeforeEndSec: cg.hideBeforeEndSec })
        const nextCg = { templateId: next.templateId ?? '', offsetSec: next.offsetSec, hideBeforeEndSec: next.hideBeforeEndSec }
        setCg(nextCg)
        setCgSaved(nextCg)
      }
      if (!sameEditable(form, saved)) adoptSettings(await slideshowApi.update(channelId, form), false)
      setMessage({ tone: 'ok', text: 'Réglages enregistrés' })
      void refreshPreview(seed)
    } catch (cause) {
      setMessage({ tone: 'error', text: errorText(cause, 'Enregistrement impossible') })
    } finally {
      setBusy(null)
    }
  }, [adoptSettings, cg, cgSaved, channelId, form, refreshPreview, saved, seed])

  const cancel = useCallback(() => {
    if (settings) setForm(editableOf(settings.config))
    setCg(cgSaved)
    setMessage(null)
  }, [cgSaved, settings])

  const chooseFile = useCallback(async (result: ProbeResult) => {
    if (!channelId || !picker) return
    const slot = picker
    setPicker(null)
    setBusy(slot)
    setMessage(null)
    try {
      const filePath = result.absolutePath ?? result.filePath
      if (slot === 'mp4') {
        const resolution = await infoservicesApi.sync({ channelId, filePath })
        setMp4(resolution)
        const updated = resolution.updatedFutureItems ?? 0
        setMessage({ tone: 'ok', text: `MP4 du jour enregistré${updated ? ` · ${updated} plage${updated > 1 ? 's' : ''} future${updated > 1 ? 's' : ''} mise${updated > 1 ? 's' : ''} à jour` : ''}` })
      } else {
        const { settings: next, warning } = await slideshowApi.setVideo(channelId, slot, filePath)
        // La transition enregistrée pour ce stinger remplace la saisie de cette transition.
        setSettings(next)
        setForm(current => {
          if (!current) return editableOf(next.config)
          if (slot === 'introStinger') return { ...current, introTransition: next.config.introTransition }
          if (slot === 'announcementStinger') return { ...current, announcementTransition: next.config.announcementTransition }
          return current
        })
        setMessage(warning ? { tone: 'warning', text: warning } : { tone: 'ok', text: 'Fichier enregistré' })
        void refreshPreview(seed)
      }
    } catch (cause) {
      setMessage({ tone: 'error', text: errorText(cause, 'Fichier refusé') })
    } finally {
      setBusy(null)
    }
  }, [channelId, picker, refreshPreview, seed])

  const removeVideo = useCallback(async (slot: SlideshowVideoSlot) => {
    if (!channelId) return
    setBusy(slot)
    setMessage(null)
    try {
      const { settings: next } = await slideshowApi.setVideo(channelId, slot, null)
      setSettings(next)
      setForm(current => {
        if (!current) return editableOf(next.config)
        if (slot === 'introStinger') return { ...current, introTransition: next.config.introTransition }
        if (slot === 'announcementStinger') return { ...current, announcementTransition: next.config.announcementTransition }
        return current
      })
      void refreshPreview(seed)
    } catch (cause) {
      setMessage({ tone: 'error', text: errorText(cause, 'Retrait impossible') })
    } finally {
      setBusy(null)
    }
  }, [channelId, refreshPreview, seed])

  const uploadTracks = useCallback(async (files: File[]) => {
    if (!channelId) return
    setMessage(null)
    for (const file of files) {
      setUploading(file.name)
      try {
        await slideshowApi.uploadTrack(channelId, file)
      } catch (cause) {
        setMessage({ tone: 'error', text: `${file.name} : ${errorText(cause, 'import impossible')}` })
        break
      }
    }
    setUploading(null)
    try {
      adoptSettings(await slideshowApi.get(channelId), true)
    } finally {
      void refreshPreview(seed)
    }
  }, [adoptSettings, channelId, refreshPreview, seed])

  const moveTrack = useCallback(async (trackId: string, delta: -1 | 1) => {
    if (!channelId || !settings) return
    const ids = settings.music.map(track => track.id)
    const index = ids.indexOf(trackId)
    const target = index + delta
    if (index < 0 || target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    setBusy('music')
    try {
      const music = await slideshowApi.reorderTracks(channelId, ids)
      setSettings(current => (current ? { ...current, music } : current))
    } catch (cause) {
      setMessage({ tone: 'error', text: errorText(cause, 'Réordonnancement impossible') })
    } finally {
      setBusy(null)
    }
  }, [channelId, settings])

  /** Morceau choisi dans le sélecteur : une copie déjà normalisée est reprise telle quelle. */
  const restoreTrack = useCallback(async (result: ProbeResult) => {
    if (!channelId) return
    setMusicPicker(false)
    setMessage(null)
    const name = result.fileName.replace(/\.[^.]+$/, '')
    setRestoring(name)
    try {
      await slideshowApi.linkTrack(channelId, result.absolutePath ?? result.filePath)
      adoptSettings(await slideshowApi.get(channelId), true)
      void refreshPreview(seed)
    } catch (cause) {
      setMessage({ tone: 'error', text: `${name} : ${errorText(cause, 'ajout impossible')}` })
    } finally {
      setRestoring(null)
    }
  }, [adoptSettings, channelId, refreshPreview, seed])

  const removeTrack = useCallback(async (track: MusicTrack) => {
    if (!channelId) return
    if (!window.confirm(`Retirer « ${track.title} » de la playlist ? Le fichier reste sur le disque.`)) return
    setBusy('music')
    try {
      const music = await slideshowApi.removeTrack(channelId, track.id)
      setSettings(current => (current ? { ...current, music, musicTotalMs: music.reduce((t, m) => t + m.durationMs, 0) } : current))
      void refreshPreview(seed)
    } catch (cause) {
      setMessage({ tone: 'error', text: errorText(cause, 'Retrait impossible') })
    } finally {
      setBusy(null)
    }
  }, [channelId, refreshPreview, seed])

  if (!channelId) return <div className="tp-empty-state">Choisissez une chaîne.</div>
  if (loading || !settings || !form) {
    return <div className="tp-empty-state slideshow-settings__state" aria-live="polite"><Spinner /> Chargement des réglages…</div>
  }

  const config = settings.config
  const savedIntroStinger = config.introTransition.kind === 'STINGER' ? config.introTransition : null
  const savedAnnouncementStinger = config.announcementTransition.kind === 'STINGER' ? config.announcementTransition : null
  const slideshowBlocked = settings.activationProblems.length > 0 && form.mode !== 'SLIDESHOW'
  const holdInvalid = form.holdThresholdSec >= form.displaySec
  const next = preview?.plage

  return (
    <div className="slideshow-settings">
      <div className="slideshow-settings__bar">
        <div className="slideshow-settings__next">
          <span className="slideshow-muted">{next?.source === 'GRID' ? 'Prochain infoservice' : 'Aucun infoservice à venir dans la grille'}</span>
          {next?.source === 'GRID' && next.startAt && (
            <strong className="slideshow-mono">
              {new Date(next.startAt).toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              {' · '}{formatSpan(next.durationMs)}
            </strong>
          )}
        </div>
        {message && <span className={`slideshow-message is-${message.tone}`} role={message.tone === 'error' ? 'alert' : 'status'}>{message.text}</span>}
        {canManage && (
          <div className="slideshow-settings__actions">
            <button type="button" className="tp-button" disabled={!dirty || busy !== null} onClick={cancel}>Annuler</button>
            <button type="button" className="tp-button tp-button--primary" disabled={!dirty || busy !== null || holdInvalid} onClick={() => void save()}>
              {busy === 'save' && <Spinner />} Enregistrer
            </button>
          </div>
        )}
      </div>

      <section className="slideshow-card slideshow-card--wide">
        <header className="slideshow-card__header">
          <div>
            <h3>Aperçu du déroulé d’une plage</h3>
            <p className="slideshow-muted">
              Réglages enregistrés{dirty ? ' (vos modifications n’y sont pas encore)' : ''} · annonces du {preview ? new Date(`${preview.day}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}
              {preview ? ` : ${preview.counts.onAir} à l’antenne, dont ${preview.counts.priorities} prioritaire${preview.counts.priorities > 1 ? 's' : ''}, sur ${preview.counts.scheduled} cochée${preview.counts.scheduled > 1 ? 's' : ''}` : ''}
            </p>
          </div>
          <button type="button" className="tp-button" onClick={() => { const nextSeed = Math.floor(Math.random() * 2 ** 31); setSeed(nextSeed); void refreshPreview(nextSeed) }}>
            <RefreshCw size={14} aria-hidden="true" /> Autre mélange
          </button>
        </header>
        {preview ? <SlideshowTimeline preview={preview} hasCg={Boolean(cgSaved.templateId)} /> : <div className="tp-empty-state">Aperçu indisponible.</div>}
      </section>

      <div className="slideshow-settings__columns">
        <div className="slideshow-settings__column">
          <section className="slideshow-card">
            <header className="slideshow-card__header">
              <div>
                <h3>Mode</h3>
                <p className="slideshow-muted">Ce que le serveur de diffusion joue pendant une plage d’infoservice, sur toutes les plages : grille, comblages et décrochages.</p>
              </div>
            </header>
            <div className="slideshow-modes" role="radiogroup" aria-label="Mode de l’infoservice">
              <button type="button" role="radio" aria-checked={form.mode === 'SLIDESHOW'}
                className={`slideshow-mode${form.mode === 'SLIDESHOW' ? ' is-active' : ''}`}
                disabled={!canManage || slideshowBlocked}
                onClick={() => set('mode', 'SLIDESHOW')}>
                <span className="slideshow-mode__title"><Images size={16} aria-hidden="true" /> Diaporama d’annonces</span>
                <span className="slideshow-mode__text">Intro, puis les images fixes des annonces du jour — les prioritaires en tête de chaque tour, puis mélange, et on boucle.</span>
                {settings.activationProblems.length > 0 && (
                  <ul className="slideshow-mode__problems">{settings.activationProblems.map(problem => <li key={problem}>{problem}</li>)}</ul>
                )}
              </button>
              <button type="button" role="radio" aria-checked={form.mode === 'VIDEO_FILE'}
                className={`slideshow-mode${form.mode === 'VIDEO_FILE' ? ' is-active' : ''}`}
                disabled={!canManage}
                onClick={() => set('mode', 'VIDEO_FILE')}>
                <span className="slideshow-mode__title"><Film size={16} aria-hidden="true" /> Fichier MP4 du jour</span>
                <span className="slideshow-mode__text">Secours : on diffuse une vidéo déposée pour la journée, à la place du diaporama.</span>
              </button>
            </div>
            <div className="slideshow-file">
              <Film size={14} aria-hidden="true" />
              <span className="slideshow-mono">{mp4?.fileName ?? 'Aucun MP4 sélectionné'}</span>
              {mp4 && <span className="slideshow-muted">{formatSpan(mp4.spot.durationSec * 1000)}</span>}
              {canManage && canEditGrid && (
                <button type="button" className="tp-button" disabled={busy !== null} onClick={() => setPicker('mp4')}>
                  {busy === 'mp4' ? <Spinner /> : <Upload size={14} aria-hidden="true" />} Choisir le MP4
                </button>
              )}
            </div>
            {form.mode !== config.mode && (
              <p className="slideshow-consequence" role="status">
                {form.mode === 'SLIDESHOW'
                  ? 'À l’enregistrement, toutes les plages d’infoservice de la chaîne passent en diaporama, y compris celle qui commence ensuite. Une plage déjà à l’antenne finit dans son mode.'
                  : 'À l’enregistrement, les plages d’infoservice reviennent au MP4 du jour. Une plage déjà à l’antenne finit dans son mode.'}
              </p>
            )}
          </section>

          <section className="slideshow-card">
            <header className="slideshow-card__header"><h3>Rythme du diaporama</h3></header>
            <div className="slideshow-rows">
              <Stepper
                label="Durée d’affichage par annonce"
                hint="Chaque image reste à l’écran ce temps-là, sauf si elle a sa propre durée dans sa fiche."
                value={form.displaySec} min={3} max={120} disabled={!canManage}
                onChange={value => set('displaySec', value)} />
              <Stepper
                label="Seuil de prolongation"
                hint="S’il reste moins que ça avant la coupure, l’annonce à l’écran y reste au lieu d’en lancer une nouvelle."
                value={form.holdThresholdSec} min={0} max={30} disabled={!canManage}
                invalid={holdInvalid ? 'Doit rester inférieur à la durée d’affichage.' : undefined}
                onChange={value => set('holdThresholdSec', value)} />
            </div>
          </section>

          <section className="slideshow-card">
            <header className="slideshow-card__header"><h3>Vidéos d’encadrement</h3></header>
            <VideoRow
              tag="Intro" required
              fileName={settings.intro.fileName} durationMs={settings.intro.durationMs}
              hint="Passe une seule fois, en tête de plage. Un changement s’applique au prochain infoservice, pas à celui en cours."
              busy={busy === 'intro'} canManage={canManage}
              onPick={() => setPicker('intro')}
              onRemove={config.mode === 'SLIDESHOW' ? undefined : () => void removeVideo('intro')} />
            <VideoRow
              tag="Outro" fileName={settings.outro.fileName} durationMs={settings.outro.durationMs}
              hint="Facultative : jouée sur les dernières secondes de la plage. Sans outro, le bloc coupe sec sur l’annonce à l’écran."
              busy={busy === 'outro'} canManage={canManage}
              onPick={() => setPicker('outro')}
              onRemove={() => void removeVideo('outro')} />
          </section>

          <section className="slideshow-card">
            <header className="slideshow-card__header">
              <div>
                <h3>Transitions</h3>
                <p className="slideshow-muted">La même transition est jouée entre toutes les annonces.</p>
              </div>
            </header>
            <TransitionEditor
              title="Intro → première annonce"
              value={form.introTransition}
              savedStinger={savedIntroStinger}
              busy={busy !== null || !canManage}
              onChange={(value: SlideshowTransition) => set('introTransition', value)}
              onPickStinger={() => setPicker('introStinger')}
              onRemoveStinger={() => void removeVideo('introStinger')} />
            <TransitionEditor
              title="Entre les annonces"
              value={form.announcementTransition}
              savedStinger={savedAnnouncementStinger}
              busy={busy !== null || !canManage}
              onChange={(value: SlideshowTransition) => set('announcementTransition', value)}
              onPickStinger={() => setPicker('announcementStinger')}
              onRemoveStinger={() => void removeVideo('announcementStinger')} />
          </section>
        </div>

        <div className="slideshow-settings__column">
          <section className="slideshow-card">
            <header className="slideshow-card__header">
              <h3>Musique de fond</h3>
              <label className="slideshow-switch">
                <input type="checkbox" checked={form.musicEnabled} disabled={!canManage} onChange={event => set('musicEnabled', event.target.checked)} />
                <span>{form.musicEnabled ? 'Activée' : 'Coupée'}</span>
              </label>
            </header>
            <MusicPlaylist
              tracks={settings.music}
              totalMs={settings.music.reduce((total, track) => total + track.durationMs, 0)}
              uploading={uploading}
              disabled={!canManage || busy !== null}
              restoring={restoring}
              onPickExisting={() => setMusicPicker(true)}
              onUpload={files => void uploadTracks(files)}
              onMove={(id, delta) => void moveTrack(id, delta)}
              onRemove={track => void removeTrack(track)} />
            <p className="slideshow-hint">Normalisation à −23 LUFS (EBU R128) à l’import ; le fichier d’origine est conservé.</p>
            <div className="slideshow-fades">
              <Stepper compact label="Fondu d’ouverture" hint="Démarre à la fin de l’intro." value={form.musicFadeInSec} min={0} max={30}
                disabled={!canManage || !form.musicEnabled} onChange={value => set('musicFadeInSec', value)} />
              <Stepper compact label="Fondu de sortie" hint="Se termine à la coupure de la plage." value={form.musicFadeOutSec} min={0} max={30}
                disabled={!canManage || !form.musicEnabled} onChange={value => set('musicFadeOutSec', value)} />
            </div>
          </section>

          <section className="slideshow-card">
            <header className="slideshow-card__header"><h3>Habillage d’infoservice</h3></header>
            <label className="slideshow-check">
              <input type="checkbox" checked={form.cgHiddenDuringIntro} disabled={!canManage} onChange={event => set('cgHiddenDuringIntro', event.target.checked)} />
              <span>
                <strong>Masqué pendant l’intro</strong>
                <small>L’habillage n’apparaît qu’à la première annonce, une fois l’intro terminée. En mode MP4, il suit les délais ci-dessous.</small>
              </span>
            </label>
            <div className="slideshow-rows">
              <label className="slideshow-field">
                <span className="tp-field-label">Modèle d’habillage</span>
                <select className="tp-input" value={cg.templateId} disabled={!canManage} onChange={event => { setCg(current => ({ ...current, templateId: event.target.value })); setMessage(null) }}>
                  <option value="">Aucun</option>
                  {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
                </select>
              </label>
              <div className="slideshow-fades">
                <Stepper compact label="Afficher après le début" value={cg.offsetSec} min={0} max={3600} disabled={!canManage || !cg.templateId}
                  onChange={value => setCg(current => ({ ...current, offsetSec: value }))} />
                <Stepper compact label="Retirer avant la fin" value={cg.hideBeforeEndSec} min={0} max={3600} disabled={!canManage || !cg.templateId}
                  onChange={value => setCg(current => ({ ...current, hideBeforeEndSec: value }))} />
              </div>
            </div>
          </section>

          <section className="slideshow-card">
            <header className="slideshow-card__header">
              <div>
                <h3>Ordre de passage</h3>
                <p className="slideshow-muted">Règle appliquée à chaque plage, non modifiable.</p>
              </div>
              <button type="button" className="tp-button" onClick={onShowAnnouncements}>
                Voir les annonces <ArrowRight size={14} aria-hidden="true" />
              </button>
            </header>
            <ol className="slideshow-rules">
              <li><strong>Les annonces prioritaires</strong><small>en tête de chaque tour, juste après l’intro au premier</small></li>
              <li><strong>Puis les autres, mélangées</strong><small>un nouveau mélange à chaque tour</small></li>
              <li><strong>Et on boucle</strong><small>jusqu’à la fin de la plage ; le jeu d’annonces est celui du jour où la plage commence</small></li>
            </ol>
            {preview && (
              <p className="slideshow-today">
                Aujourd’hui : <strong>{preview.counts.onAir} à l’antenne</strong> sur {preview.counts.scheduled} cochée{preview.counts.scheduled > 1 ? 's' : ''}
                {preview.counts.priorities > 0 && <span className="slideshow-pill is-accent">{preview.counts.priorities} prioritaire{preview.counts.priorities > 1 ? 's' : ''}</span>}
              </p>
            )}
          </section>
        </div>
      </div>

      {picker && (
        <MediaBrowserModal onSelect={result => void chooseFile(result)} onClose={() => setPicker(null)} confirmLabel={PICKER_LABELS[picker]} />
      )}
      {musicPicker && (
        <MediaBrowserModal audioOnly initialPath={MUSIC_FOLDER} confirmLabel="Ajouter à la playlist"
          onSelect={result => void restoreTrack(result)} onClose={() => setMusicPicker(false)} />
      )}
    </div>
  )
})

const Stepper = memo(function Stepper({ label, hint, value, min, max, disabled, invalid, compact, onChange }: {
  label: string; hint?: string; value: number; min: number; max: number; disabled?: boolean; invalid?: string; compact?: boolean
  onChange: (value: number) => void
}) {
  const clamp = (next: number) => Math.min(max, Math.max(min, Math.round(next)))
  return (
    <div className={`slideshow-stepper${compact ? ' is-compact' : ''}`}>
      <div className="slideshow-stepper__text">
        <span className="slideshow-stepper__label">{label}</span>
        {hint && <span className="slideshow-muted">{hint}</span>}
        {invalid && <span className="slideshow-stepper__error" role="alert">{invalid}</span>}
      </div>
      <div className="slideshow-stepper__control">
        <button type="button" className="tp-icon-button" disabled={disabled || value <= min} onClick={() => onChange(clamp(value - 1))} aria-label={`${label} : moins`}>−</button>
        <input className={`tp-input${invalid ? ' is-invalid' : ''}`} type="number" min={min} max={max} value={value} disabled={disabled} aria-label={label}
          onChange={event => { const next = Number(event.target.value); if (Number.isFinite(next)) onChange(clamp(next)) }} />
        <span className="slideshow-muted" aria-hidden="true">s</span>
        <button type="button" className="tp-icon-button" disabled={disabled || value >= max} onClick={() => onChange(clamp(value + 1))} aria-label={`${label} : plus`}>+</button>
      </div>
    </div>
  )
})

const VideoRow = memo(function VideoRow({ tag, required, fileName, durationMs, hint, busy, canManage, onPick, onRemove }: {
  tag: string; required?: boolean; fileName: string | null; durationMs: number | null; hint: string
  busy: boolean; canManage: boolean; onPick: () => void; onRemove?: () => void
}) {
  return (
    <div className="slideshow-video">
      <span className="slideshow-video__thumb" aria-hidden="true"><Film size={18} /></span>
      <div className="slideshow-video__text">
        <span className="slideshow-video__name">
          <span className="slideshow-pill">{tag}</span>
          {fileName ? <span className="slideshow-mono">{fileName}</span> : <span className="slideshow-muted">{required ? 'À choisir — indispensable au diaporama' : 'Aucune'}</span>}
          {durationMs !== null && <span className="slideshow-muted">{formatSeconds(durationMs)}</span>}
        </span>
        <span className="slideshow-muted">{hint}</span>
      </div>
      {canManage && (
        <span className="slideshow-video__actions">
          <button type="button" className="tp-button" disabled={busy} onClick={onPick}>
            {busy ? <Spinner /> : <Upload size={14} aria-hidden="true" />} {fileName ? 'Changer' : 'Choisir un fichier'}
          </button>
          {fileName && onRemove && (
            <button type="button" className="tp-icon-button" disabled={busy} onClick={onRemove} aria-label={`Retirer l’${tag.toLowerCase()}`}>
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </span>
      )}
    </div>
  )
})
